import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import { LogOut, RefreshCw, Clock, FileText, FolderOpen, BarChart3, Shield, X, Save } from 'lucide-react'
import { toast } from 'sonner'

export default function AdminDashboard() {
  const { admin, token, logout } = useAuth()
  const [documentsCount, setDocumentsCount] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  useEffect(() => {
    loadData()
  }, [token])

  const loadData = async () => {
    if (!token) return
    setIsLoading(true)
    try {
      const docs = await api.getAdminDocuments(token)
      setDocumentsCount(docs.length)
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
      toast.error('Erro ao carregar estatísticas')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    toast.success('Logout realizado')
  }

  const resetPasswordForm = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
  }

  const handleChangePassword = async () => {
    if (!token) return

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Preencha todos os campos')
      return
    }
    if (newPassword.length < 8) {
      toast.error('A nova senha deve ter no mínimo 8 caracteres')
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error('A confirmação não confere com a nova senha')
      return
    }

    setIsChangingPassword(true)
    try {
      await api.changePassword(token, currentPassword, newPassword)
      toast.success('Senha alterada com sucesso!')
      setIsPasswordModalOpen(false)
      resetPasswordForm()
    } catch (error: any) {
      toast.error(error.message || 'Erro ao alterar senha')
    } finally {
      setIsChangingPassword(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-serif font-bold text-[#0d2d47]">Painel Administrativo</h1>
            <p className="text-sm text-gray-500">Bem-vindo, {admin?.name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            Sair
          </button>
        </div>
      </header>

      <div className="p-6">
        {/* Cards de estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Card de Documentos */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Documentos Publicados</p>
                <p className="text-3xl font-bold text-[#0d2d47] mt-1">
                  {isLoading ? (
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-[#0d6b3d] rounded-full animate-spin" />
                  ) : (
                    documentsCount ?? 0
                  )}
                </p>
              </div>
              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center">
                <FolderOpen size={24} className="text-indigo-500" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Documentos disponíveis para download no site
            </p>
          </div>

          {/* Card de Último Acesso */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Último Acesso</p>
                <p className="text-sm font-medium text-[#0d2d47] mt-1">
                  {new Date().toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                <Clock size={24} className="text-[#0d6b3d]" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              {admin?.email}
            </p>
          </div>

          {/* Card de Total de Documentos (alternativo) */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Categorias</p>
                <p className="text-3xl font-bold text-[#0d2d47] mt-1">
                  {isLoading ? (
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-[#0d6b3d] rounded-full animate-spin" />
                  ) : (
                    '5'
                  )}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                <FileText size={24} className="text-amber-500" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Categorias de documentos disponíveis
            </p>
          </div>
        </div>

        {/* Módulos disponíveis */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <a
            href="/admin/stages"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:border-[#0d6b3d]/30 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#0d6b3d]/10 rounded-lg flex items-center justify-center group-hover:bg-[#0d6b3d] transition-colors">
                <BarChart3 size={20} className="text-[#0d6b3d] group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-[#0d2d47]">Gerenciar Etapas</h3>
            </div>
            <p className="text-sm text-gray-500">
              Altere a fase atual do projeto e acompanhe o progresso das etapas.
            </p>
            <p className="text-xs text-[#0d6b3d] mt-3 font-medium group-hover:underline">
              Gerenciar →
            </p>
          </a>

          <a
            href="/admin/documents"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:border-indigo-500/30 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center group-hover:bg-indigo-500 transition-colors">
                <FileText size={20} className="text-indigo-500 group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-[#0d2d47]">Documentos</h3>
            </div>
            <p className="text-sm text-gray-500">
              Gerencie os documentos públicos do projeto. Faça upload, edite ou remova arquivos.
            </p>
            <p className="text-xs text-indigo-600 mt-3 font-medium group-hover:underline">
              Gerenciar documentos →
            </p>
          </a>

          <a
            href="/admin/logs"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:border-gray-300 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                <Clock size={20} className="text-gray-500 group-hover:text-gray-700" />
              </div>
              <h3 className="font-semibold text-[#0d2d47]">Logs do Sistema</h3>
            </div>
            <p className="text-sm text-gray-500">
              Visualize o histórico de ações dos administradores no sistema.
            </p>
            <p className="text-xs text-gray-500 mt-3 font-medium group-hover:underline">
              Ver logs →
            </p>
          </a>

          <a
            href="/admin/consulta"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:border-[#f1b90f]/40 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#f1b90f]/10 rounded-lg flex items-center justify-center group-hover:bg-[#f1b90f] transition-colors">
                <FileText size={20} className="text-[#b8890b] group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-[#0d2d47]">Consulta Pública</h3>
            </div>
            <p className="text-sm text-gray-500">
              Veja as contribuições recebidas pelo formulário público e exporte em CSV.
            </p>
            <p className="text-xs text-[#b8890b] mt-3 font-medium group-hover:underline">
              Ver contribuições →
            </p>
          </a>

          <a
            href="/admin/content"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:border-purple-500/30 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center group-hover:bg-purple-500 transition-colors">
                <FolderOpen size={20} className="text-purple-500 group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-[#0d2d47]">Conteúdo do Site</h3>
            </div>
            <p className="text-sm text-gray-500">
              Edite os textos do topo, desafios, cronograma e base legal exibidos na página.
            </p>
            <p className="text-xs text-purple-600 mt-3 font-medium group-hover:underline">
              Editar conteúdo →
            </p>
          </a>

          <button
            type="button"
            onClick={() => setIsPasswordModalOpen(true)}
            className="text-left bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:border-red-300 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-500 transition-colors">
                <Shield size={20} className="text-red-500 group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-[#0d2d47]">Segurança</h3>
            </div>
            <p className="text-sm text-gray-500">
              Altere a senha de acesso ao painel administrativo.
            </p>
            <p className="text-xs text-red-600 mt-3 font-medium group-hover:underline">
              Trocar senha →
            </p>
          </button>
        </div>

        {/* Botão de atualizar */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={loadData}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-[#0d6b3d] transition-colors"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            Atualizar dados
          </button>
        </div>
      </div>

      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-serif font-bold text-[#0d2d47]">Alterar Senha</h2>
              <button
                onClick={() => { setIsPasswordModalOpen(false); resetPasswordForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha atual *</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0d6b3d] focus:border-transparent"
                  autoComplete="current-password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova senha *</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0d6b3d] focus:border-transparent"
                  placeholder="Mínimo 8 caracteres"
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar nova senha *</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0d6b3d] focus:border-transparent"
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
              <button
                onClick={() => { setIsPasswordModalOpen(false); resetPasswordForm(); }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleChangePassword}
                disabled={isChangingPassword}
                className="flex items-center gap-2 px-4 py-2 bg-[#0d6b3d] text-white rounded-lg hover:bg-[#0b5b35] transition-colors disabled:opacity-60"
              >
                {isChangingPassword ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Salvar Nova Senha
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}